'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import { checkVies } from '@eliadmin/core/src/api/vies/vies-api';
import { REGISTER_COPY } from '@eliadmin/core/src/components/dlz/auth/register/register-copy';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

type LookupStatus = 'idle' | 'loading' | 'done';
type CompanyField = 'company' | 'dic' | 'ic_dph' | 'address' | 'city' | 'postcode' | 'country';

/** Form paths the lookup reads (`ico`) and fills – the registration's by default. */
export type CompanyLookupFields = Record<CompanyField | 'ico', string>;

const REGISTER_FIELDS: CompanyLookupFields = {
  ico: 'ico',
  company: 'company',
  dic: 'dic',
  ic_dph: 'ic_dph',
  address: 'address',
  city: 'city',
  postcode: 'postcode',
  country: 'country',
};

/**
 * „Vyhľadať“ next to the IČO: asks the only company lookup the storefront API has (`/store/vies`,
 * the one the checkout uses for the VAT id) and fills the company fields it returns. Whether the
 * endpoint resolves an IČO – the design says „z obchodného registra“ – needs backend confirmation.
 */
export function useCompanyLookup(countries: Country[], fields = REGISTER_FIELDS) {
  const locale = useLocaleStore(state => state.currentLocale);
  const { getValues, setValue, setError, clearErrors, trigger } = useFormContext();
  const [status, setStatus] = useState<LookupStatus>('idle');
  const request = useRef<AbortController | null>(null);

  useEffect(() => () => request.current?.abort(), []);

  const lookup = useCallback(async () => {
    const term = String(getValues(fields.ico) ?? '').trim();
    if (!term) {
      await trigger(fields.ico);
      return;
    }

    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    clearErrors(fields.ico);
    setStatus('loading');

    try {
      const result = await checkVies({ locale, term, signal: controller.signal });
      if (controller.signal.aborted) return;

      if (!result.company_name) {
        setError(fields.ico, { type: 'lookup', message: REGISTER_COPY.company.lookupNotFound });
        setStatus('idle');
        return;
      }

      const fill = (name: CompanyField, value: string | null | undefined) => {
        if (value) setValue(fields[name], value, { shouldDirty: true, shouldValidate: true });
      };
      const { address } = result;
      fill('company', result.company_name);
      fill('dic', result.dic);
      fill('ic_dph', result.ic_dph);
      fill('address', [address?.address, address?.street_number].filter(Boolean).join(' '));
      fill('city', address?.city);
      fill('postcode', address?.postcode);
      if (countries.some(country => country.id === result.country_id)) {
        fill('country', result.country_id);
      }
      setStatus('done');
    } catch {
      if (controller.signal.aborted) return;
      setError(fields.ico, { type: 'lookup', message: REGISTER_COPY.company.lookupFailed });
      setStatus('idle');
    }
  }, [clearErrors, countries, fields, getValues, locale, setError, setValue, trigger]);

  return { status, lookup };
}
